import type { ApprovalStatus } from '@fdh/shared';
import { APPROVAL_STATUS } from '@fdh/shared';
import { EmployerOverdueStatusIcon } from './EmployerOverdueStatusIcon';

type EmployerHistoryStatusIconProps = {
  status: ApprovalStatus;
  isAwaitingSubmission: boolean;
  isOverdue?: boolean;
  variant?: 'card' | 'list';
};

export const EmployerHistoryStatusIcon = ({
  status,
  isAwaitingSubmission,
  isOverdue = false,
  variant = 'card',
}: EmployerHistoryStatusIconProps) => {
  const isList = variant === 'list';
  const boxClass = isList ? 'h-11 w-11 rounded-full' : 'h-10 w-10 rounded-full';
  const iconClass = isList ? 'h-5 w-5' : 'h-[18px] w-[18px]';

  if (isOverdue) {
    return <EmployerOverdueStatusIcon variant={variant} />;
  }

  if (isAwaitingSubmission) {
    return (
      <div
        className={`flex shrink-0 items-center justify-center bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 ${boxClass}`}
        aria-hidden
      >
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.1 2.182a10 10 0 0 1 3.8 0m0 19.636a10 10 0 0 1-3.8 0m7.509-18.097a10 10 0 0 1 2.69 2.7M2.182 13.9a10 10 0 0 1 0-3.8m18.097 7.509a10 10 0 0 1-2.7 2.69M21.818 10.1a10 10 0 0 1 0 3.8M3.721 6.391a10 10 0 0 1 2.7-2.69m-.03 16.578a10 10 0 0 1-2.69-2.7"
          />
        </svg>
      </div>
    );
  }

  if (status === APPROVAL_STATUS.APPROVED) {
    return (
      <div
        className={`flex shrink-0 items-center justify-center bg-emerald-100 text-emerald-600 dark:bg-emerald-950/55 dark:text-emerald-400 ${boxClass}`}
        aria-hidden
      >
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  }

  if (status === APPROVAL_STATUS.PENDING) {
    return (
      <div
        className={`flex shrink-0 items-center justify-center bg-amber-100 text-amber-700 dark:bg-amber-950/45 dark:text-amber-300 ${boxClass}`}
        aria-hidden
      >
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
    );
  }

  if (status === APPROVAL_STATUS.NEEDS_UPDATE) {
    return (
      <div
        className={`flex shrink-0 items-center justify-center bg-orange-100 text-orange-600 dark:bg-orange-950/50 dark:text-orange-300 ${boxClass}`}
        aria-hidden
      >
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
    );
  }

  if (status === APPROVAL_STATUS.CANCEL) {
    return (
      <div
        className={`flex shrink-0 items-center justify-center bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 ${boxClass}`}
        aria-hidden
      >
        <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
          />
        </svg>
      </div>
    );
  }

  return (
    <div
      className={`flex shrink-0 items-center justify-center bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 ${boxClass}`}
      aria-hidden
    >
      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </div>
  );
};
